import { zodResolver } from "@hookform/resolvers/zod";
import type React from "react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import {
  createReportSchema,
  type ReportFormValues,
} from "@/components/common/reportDialogSchema";
import {
  getReportReasonFallback,
  REPORT_REASONS,
} from "@/components/common/reportReasons";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

interface ReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: number, details: string) => Promise<void>;
  isSubmitting: boolean;
}

export const ReportDialog: React.FC<ReportDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  const schema = useMemo(() => createReportSchema(t), [t]);

  const form = useForm<ReportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      reason: undefined,
      details: "",
    },
  });

  const { handleSubmit, reset } = form;

  const onFormSubmit = handleSubmit(async (values) => {
    const selectedReasonObject = REPORT_REASONS.find(
      (reason) => reason.id === values.reason,
    );
    const reasonLabel = selectedReasonObject
      ? t(selectedReasonObject.labelKey)
      : getReportReasonFallback(values.reason);
    const detailsToSubmit = values.details?.trim() || reasonLabel;

    await onSubmit(values.reason, detailsToSubmit);
    reset({ reason: undefined, details: "" });
  });
  const handleModalClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <BasicModal isOpen={isOpen} onClose={handleModalClose} maxWidth="max-w-md">
      <div className="border-b p-4 pr-14">
        <h2 className="text-lg font-medium">{t("common.reportPost")}</h2>
      </div>
      <Form {...form}>
        <form onSubmit={onFormSubmit} className="space-y-6 p-4">
          <p className="font-medium">
            {t(
              "common.reportPrompt",
              "What concerns do you have about this post?",
            )}
          </p>

          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormMessage />
                <div className="space-y-2">
                  {REPORT_REASONS.map((reason) => (
                    <label
                      key={reason.id}
                      className="flex items-start gap-2 text-sm"
                    >
                      <FormControl>
                        <input
                          type="radio"
                          value={reason.id}
                          checked={field.value === reason.id}
                          onChange={() => field.onChange(reason.id)}
                          disabled={isSubmitting}
                          className="mt-1"
                        />
                      </FormControl>
                      {t(reason.labelKey)}
                    </label>
                  ))}
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="details"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("common.giveMoreDetails", "Give us more details.")}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    className="min-h-[100px]"
                    placeholder={t("common.reportDetailsPlaceholder")}
                    disabled={isSubmitting}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleModalClose}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="teal"
              type="submit"
              disabled={isSubmitting || !form.formState.isValid}
            >
              {isSubmitting ? t("common.loading") : t("common.send")}
            </Button>
          </div>
        </form>
      </Form>
    </BasicModal>
  );
};
